import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { listAllPending, removePending, markAttempt, STORE_ENDPOINTS, STORE_LABELS } from "@/lib/offlineQueue";
import { toast } from "sonner";

// Verrou global anti-doublons (partagé entre toutes les instances du hook)
const SYNCING_IDS = new Set();
let GLOBAL_SYNC_LOCK = false;

/**
 * Hook global : surveille navigator.onLine et synchronise toutes les files offline
 * (timesheets, absences, frais, notes). Retourne { online, pendingCount, syncing, syncNow }.
 */
export function useOfflineSync({ onSynced } = {}) {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const onSyncedRef = useRef(onSynced);
  useEffect(() => { onSyncedRef.current = onSynced; }, [onSynced]);

  const refreshCount = useCallback(async () => {
    try {
      const all = await listAllPending();
      setPendingCount(Object.values(all).reduce((n, arr) => n + arr.length, 0));
    } catch { /* IDB unavailable */ }
  }, []);

  const syncNow = useCallback(async () => {
    // Verrou global : une seule exécution concurrente possible même avec plusieurs instances
    if (GLOBAL_SYNC_LOCK) return 0;
    if (!navigator.onLine) return 0;
    GLOBAL_SYNC_LOCK = true;
    setSyncing(true);
    let synced = 0; let failed = 0;
    try {
      // On ne synchronise QUE les items appartenant à l'utilisateur courant.
      // Les items d'un autre user_id restent en file et ne seront traités qu'après connexion de ce user.
      const all = await listAllPending();
      for (const [storeName, items] of Object.entries(all)) {
        const endpoint = STORE_ENDPOINTS[storeName];
        if (!endpoint) continue;
        for (const rec of items) {
          // Verrou par item : évite les doubles POST si plusieurs cycles de sync se chevauchent
          const lockKey = `${storeName}:${rec.local_id}`;
          if (SYNCING_IDS.has(lockKey)) continue;
          SYNCING_IDS.add(lockKey);
          try {
            if (rec.payload && rec.payload.__multipart) {
              const fd = new FormData();
              Object.entries(rec.payload).forEach(([k, v]) => {
                if (k === "__multipart") return;
                if (k === "photo_data" && v) {
                  const [meta, b64] = v.split(",");
                  const mime = (meta.match(/data:([^;]+)/) || [])[1] || "image/jpeg";
                  const bin = atob(b64);
                  const u8 = new Uint8Array(bin.length);
                  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                  fd.append("photo", new Blob([u8], { type: mime }), "photo.jpg");
                } else if (v !== undefined && v !== null) {
                  fd.append(k, String(v));
                }
              });
              await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
            } else {
              await api.post(endpoint, rec.payload);
            }
            await removePending(storeName, rec.local_id);
            synced++;
          } catch (err) {
            await markAttempt(storeName, rec.local_id, err?.response?.data?.detail || err?.message);
            failed++;
          } finally {
            SYNCING_IDS.delete(lockKey);
          }
        }
      }
      if (synced > 0) {
        toast.success(`${synced} enregistrement${synced > 1 ? "s" : ""} synchronisé${synced > 1 ? "s" : ""}`);
        onSyncedRef.current?.(synced);
      }
      if (failed > 0) toast.error(`${failed} en échec — réessai au prochain cycle`);
    } finally {
      GLOBAL_SYNC_LOCK = false;
      setSyncing(false);
      refreshCount();
    }
    return synced;
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    const onOnline = () => { setOnline(true); syncNow(); };
    const onOffline = () => setOnline(false);
    const onQueueChanged = () => { refreshCount(); };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("oscop-offline-queue-changed", onQueueChanged);
    if (navigator.onLine) syncNow();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("oscop-offline-queue-changed", onQueueChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { online, pendingCount, syncing, syncNow, refreshCount };
}

export { STORE_LABELS };
